"use client";

import {
  createContext,
  FC,
  PropsWithChildren,
  useMemo,
} from "react";

import { CheckoutAPI } from "@api-clients/checkout";
import { SufQuizAnswersAPI } from "@api-clients/suf-quiz-answers";
import { CustomerAPI } from "@api-clients/customer";
import { DiscountsAPI } from "@api-clients/discounts";
import { AddressesAPI } from "@api-clients/addresses";
import { FreshMealPlanAPI } from "apps/website/api";
import { useAuth } from "@auth/client-sdk-react";
import { CapiClient } from "@api-clients/capi";
import { LitterAPI } from "@api-clients/litter";
import { OrdersAPI } from "@api-clients/orders";
import {
  PaymentMethodsAPI,
} from "libs/api/payment-methods/src/lib/PaymentMethodsApi";
import {
  DeliveryDatesAPI,
} from "libs/api/delivery-dates/src/lib/DeliveryDatesAPI";
import { SubscriptionsAPI } from "@api-clients/subscriptions";
import { CatsAPI } from "@api-clients/cats";
import { CustomerManagementAPI } from "@api-clients/customer-management";

import { PublicConfig } from "../config/public";
import { FormsActionsAPI } from "../api/FormsActions";
import { Statistics } from "../api/Statistics";
import { SurveyAnswers } from "../api/SurveyAnswers";

export interface ApiClientsContext {
  Checkout: CheckoutAPI;
  FreshMealPlan: FreshMealPlanAPI;
  Customer: CustomerAPI;
  FormActions: FormsActionsAPI;
  Capi: CapiClient;
  Statistics: Statistics;
  Discounts: DiscountsAPI;
  SurveyAnswers: SurveyAnswers;
  Litter: LitterAPI;
  Orders: OrdersAPI;
  PaymentMethods: PaymentMethodsAPI;
  Addresses: AddressesAPI;
  DeliveryDates: DeliveryDatesAPI;
  tokenStatus: {
    accessToken: string | undefined;
    loggedIn: boolean | undefined;
  };
  Subscriptions: SubscriptionsAPI;
  Cats: CatsAPI;
  SufQuizAnswers: SufQuizAnswersAPI;
  CustomerManagement: CustomerManagementAPI;
}

export const apiContext = createContext<ApiClientsContext>(undefined as unknown as ApiClientsContext);

// Site-wide context for customer data (subscriptions, cats, etc.)
export const KatkinCustomerApiProvider: FC<PropsWithChildren> = ({ children }) => {

  const { loggedIn, accessToken } = useAuth();

  const clients = useMemo(() => ({
    Checkout: new CheckoutAPI(PublicConfig.NEXT_PUBLIC_CHECKOUT_URL, accessToken),
    FreshMealPlan: new FreshMealPlanAPI(PublicConfig.NEXT_PUBLIC_OLD_API_BASE_URL, accessToken),
    FormActions: new FormsActionsAPI(PublicConfig.NEXT_PUBLIC_FORM_ACTIONS_URL, accessToken),
    Customer: new CustomerAPI(
      PublicConfig.NEXT_PUBLIC_OLD_API_BASE_URL,
      accessToken,
      { withCredentials: true },
    ),
    Discounts: new DiscountsAPI(
      PublicConfig.NEXT_PUBLIC_DISCOUNT_API_BASE_URL,
      accessToken,
    ),
    Capi: new CapiClient(
      PublicConfig.NEXT_PUBLIC_CAPI_BASE_URL,
      accessToken,
    ),
    Litter: new LitterAPI(PublicConfig.NEXT_PUBLIC_OLD_API_BASE_URL, accessToken),
    Orders: new OrdersAPI(PublicConfig.NEXT_PUBLIC_ORDERS_BASE_URL, accessToken),
    PaymentMethods: new PaymentMethodsAPI(PublicConfig.NEXT_PUBLIC_PAYMENT_METHOD_API_BASE_URL, accessToken),
    Statistics: new Statistics(PublicConfig.NEXT_PUBLIC_STATISTICS_URL, accessToken),
    SurveyAnswers: new SurveyAnswers(PublicConfig.NEXT_PUBLIC_SURVEY_ANSWERS_BASE_URL, accessToken),
    DeliveryDates: new DeliveryDatesAPI(PublicConfig.NEXT_PUBLIC_DELIVERY_DATES_API_BASE_URL, accessToken),
    tokenStatus: {
      accessToken,
      loggedIn,
    },
    Addresses: new AddressesAPI(
      PublicConfig.NEXT_PUBLIC_ADDRESSES_BASE_URL,
      accessToken,
    ),
    Subscriptions: new SubscriptionsAPI(PublicConfig.NEXT_PUBLIC_SUBSCRIPTIONS_BASE_URL, accessToken),
    Cats: new CatsAPI(PublicConfig.NEXT_PUBLIC_CATS_BASE_URL, accessToken),
    SufQuizAnswers: new SufQuizAnswersAPI(PublicConfig.NEXT_PUBLIC_SUF_QUIZ_ANSWERS_BASE_URL, accessToken),
    CustomerManagement: new CustomerManagementAPI(
      PublicConfig.NEXT_PUBLIC_CUSTOMER_MANAGEMENT_API_BASE_URL,
      accessToken,
    ),
  }), [ loggedIn, accessToken ]);

  return (
    <apiContext.Provider value={clients}>
      { children }
    </apiContext.Provider>
  );
};
